// app/src/lib/scale/freeTier.js — central gate for free-tier UI behavior.
//
// The free tier is intentionally narrow: the operator sees Maintenance
// (Rolodex), Reports → Market tile, and Account. Everything else either
// disappears from the nav or renders an upgrade card.
//
// All gates flow through pure functions in this file. UI sites call
// `isFreeTier(tier)` (or the more semantic helpers) instead of inlining
// the tier string check, so the policy lives in one place.

export const FREE = 'free';
export const PAID_TIERS = new Set(['basic', 'pro', 'payg']);

/**
 * True when the operator's plan is free / unset. Treats null / undefined /
 * empty as free so a freshly-signed-up user who hasn't had a tier stamped
 * yet sees the free experience (not a flash of paid UI).
 *
 * @param {string|null|undefined} tier
 */
export function isFreeTier(tier) {
  if (!tier) return true;
  return String(tier).toLowerCase() === FREE;
}

/** True when the operator is on any paid tier. */
export function isPaidTier(tier) {
  if (!tier) return false;
  return PAID_TIERS.has(String(tier).toLowerCase());
}

// ── Per-feature gates ─────────────────────────────────────────────────
// Each gate is a pure function of the resolved tier (and any other inputs
// the caller already has). Add a new gate here when a new feature needs
// to vary by tier — don't inline tier checks elsewhere.

/** Provider ratings are paid-only (free users see contacts without ratings). */
export function showsRating(tier) { return !isFreeTier(tier); }

/** Cross-device cloud-blob sync is paid-only. */
export function showsCloudSync(tier) { return !isFreeTier(tier); }

/**
 * Team sharing is paid-only AND requires the team add-on. The caller
 * (Account screen, Team page) passes the team-add-on state from the
 * profile so this stays a pure function.
 */
export function showsTeamShare(tier, teamAddOnEnabled) {
  return !isFreeTier(tier) && !!teamAddOnEnabled;
}

// ── Nav tabs the free tier sees in the bottom nav ─────────────────────
// Maintenance + Reports only. Account is reached via the avatar chip.
// Used by Shell.jsx to filter the items[] array.
export const FREE_NAV_TABS = new Set(['maintenance', 'reports']);

/**
 * Filter the full nav-items list down to what the operator should see.
 * Paid tiers see the full list; free sees only Maintenance + Reports.
 * The active tab is always included so a deep-linked free user lands on
 * something visible.
 *
 * @param {Array<{ id: string }>} items
 * @param {string} tier
 * @param {string} activeId
 */
export function filterNavItems(items, tier, activeId) {
  if (!isFreeTier(tier)) return items;
  return items.filter((it) => FREE_NAV_TABS.has(it.id) || it.id === activeId);
}

// ── Direct-URL gate ───────────────────────────────────────────────────
// Routes that are NOT permitted for free. When a free user navigates to
// one of these (typed URL, deep link, bookmark), the route should render
// an upgrade card instead of the gated feature. Maintenance and Reports
// stay accessible — they're the free product.
const FREE_ALLOWED_ROUTE_PREFIXES = [
  '/maintenance', '/reports', '/account', '/help', '/auth',
];

/**
 * True if a free user is allowed to render this route. Paid tiers can
 * always render any route.
 */
export function canAccessRoute(route, tier) {
  if (!isFreeTier(tier)) return true;
  if (!route) return true;
  return FREE_ALLOWED_ROUTE_PREFIXES.some((p) => route === p || route.startsWith(p + '/'));
}
